import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { AlertCircle, Loader2, MailCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AuthPageShell } from '@/components/app/auth-page-shell';
import { ApiError, forgotPasswordRequest } from '@/lib/api';

const schema = z.object({
  email: z.string().min(1, 'forgotPassword.emailRequired').email('forgotPassword.emailInvalid'),
});

type FormValues = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    clearErrors('root');
    try {
      await forgotPasswordRequest(data.email);
      // Enumeration-safe: the success state is identical for every email,
      // regardless of whether the account exists.
      setSent(true);
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        setError('root', { message: t('forgotPassword.rateLimited') });
      } else {
        toast.error(t('forgotPassword.unreachable'));
      }
    }
  };

  return (
    <AuthPageShell>
      {sent ? (
        <div className="mt-9">
          <div
            className="flex items-start gap-3 rounded-[9px] border border-status-active/30 bg-status-active/10 px-4 py-4"
            role="status"
          >
            <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-status-active" aria-hidden="true" />
            <div>
              <h1 className="text-[17px] font-semibold leading-snug text-foreground">
                {t('forgotPassword.successTitle')}
              </h1>
              <p className="mt-1.5 text-sm leading-snug text-muted-foreground">
                {t('forgotPassword.successBody')}
              </p>
            </div>
          </div>
          <p className="mt-6 text-center text-sm">
            <Link
              to="/login"
              className="rounded-[4px] font-medium text-primary underline-offset-4 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('forgotPassword.backToLogin')}
            </Link>
          </p>
        </div>
      ) : (
        <>
          <h1 className="mt-9 text-[26px] font-semibold leading-tight tracking-tight text-foreground">
            {t('forgotPassword.title')}
          </h1>
          <p className="mt-2 font-serif text-[16px] italic leading-snug text-muted-foreground">
            {t('forgotPassword.subtitle')}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">{t('forgotPassword.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t('forgotPassword.emailPlaceholder')}
                disabled={isSubmitting}
                autoFocus
                className="h-11"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive" role="alert">
                  {t(String(errors.email.message))}
                </p>
              )}
            </div>

            {errors.root && (
              <div
                className="flex items-start gap-2.5 rounded-[9px] border border-destructive/30 bg-destructive/10 px-3.5 py-3"
                role="alert"
              >
                <AlertCircle
                  className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                  aria-hidden="true"
                />
                <p className="text-sm leading-snug text-foreground">{errors.root.message}</p>
              </div>
            )}

            <Button type="submit" className="h-11 w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  {t('forgotPassword.submitting')}
                </>
              ) : (
                t('forgotPassword.submit')
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm">
            <Link
              to="/login"
              className="rounded-[4px] font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('forgotPassword.backToLogin')}
            </Link>
          </p>
        </>
      )}
    </AuthPageShell>
  );
}
